import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { withdrawalService } from '../../lib/api';

const HistoriqueRetraitsTab = () => {
  const [retraits, setRetraits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 10;

  const fetchRetraits = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      setError(null);
      const skip = reset ? 0 : page * limit;
      const data = await withdrawalService.getWithdrawalHistory(skip, limit);
      
      if (reset) {
        setRetraits(data.withdrawals || []);
        setPage(0);
      } else {
        setRetraits(prev => [...prev, ...(data.withdrawals || [])]);
      }
      
      setHasMore((data.withdrawals || []).length === limit);
    } catch (err) {
      console.error('Erreur chargement historique:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchRetraits(true);
  }, []);

  const loadMore = () => {
    setPage(prev => prev + 1);
    fetchRetraits();
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montant || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      success: { label: 'Réussi', color: 'bg-green-100 text-green-800', icon: '✅' },
      failed: { label: 'Échoué', color: 'bg-red-100 text-red-800', icon: '❌' }
    };

    const config = statusConfig[status] || statusConfig.failed;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  const getWalletIcon = (walletType) => {
    return walletType === 'orange_money' ? '🟠' : '🟡';
  };

  if (loading && retraits.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="historique-retraits-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <span className="mr-2">📋</span>
              Historique des Retraits
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchRetraits(true)}
              disabled={loading}
            >
              {loading ? '...' : '🔄 Actualiser'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
              {error}
            </div>
          )}

          {retraits.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📭</div>
              <h3 className="text-lg font-medium text-gray-900">Aucun retrait</h3>
              <p className="text-gray-500 mt-1">
                Vous n'avez pas encore effectué de retrait instantané.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Version Desktop - Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wallet</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Commission</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net reçu</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {retraits.map((retrait) => (
                      <tr key={retrait.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(retrait.created_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-900">{retrait.reference}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="mr-2">{getWalletIcon(retrait.wallet_type)}</span>
                            <span className="text-sm text-gray-600">{retrait.wallet_number}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                          {formatMontant(retrait.montant_brut)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-orange-600">
                          -{formatMontant(retrait.commission)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-bold text-green-600">
                          {formatMontant(retrait.montant_net)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {getStatusBadge(retrait.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Version Mobile - Cards */}
              <div className="md:hidden space-y-3">
                {retraits.map((retrait) => (
                  <Card key={retrait.id} className="border">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-xs text-gray-500">{formatDate(retrait.created_at)}</p>
                          <p className="text-sm font-mono text-gray-700">{retrait.reference}</p>
                        </div>
                        {getStatusBadge(retrait.status)}
                      </div>
                      
                      <div className="flex items-center mb-3 pb-3 border-b">
                        <span className="text-xl mr-2">{getWalletIcon(retrait.wallet_type)}</span>
                        <span className="text-sm text-gray-600">{retrait.wallet_number}</span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-xs text-gray-500">Demandé</p>
                          <p className="text-sm font-medium">{formatMontant(retrait.montant_brut)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Commission</p>
                          <p className="text-sm font-medium text-orange-600">-{formatMontant(retrait.commission)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Reçu</p>
                          <p className="text-sm font-bold text-green-600">{formatMontant(retrait.montant_net)}</p>
                        </div>
                      </div>
                      
                      {retrait.error_message && (
                        <div className="mt-3 p-2 bg-red-50 rounded text-xs text-red-700">
                          {retrait.error_message}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center mt-4">
                  <Button
                    variant="outline"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? 'Chargement...' : 'Charger plus'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Légende des statuts */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <h4 className="font-medium text-gray-900 mb-3">📌 Légende des statuts</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center space-x-2">
              <span className="text-lg">✅</span>
              <span className="text-sm text-gray-600">Réussi - Versé sur votre wallet</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-lg">❌</span>
              <span className="text-sm text-gray-600">Échoué - Transaction non effectuée</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoriqueRetraitsTab;
